// Offices map — a light Leaflet map that drops a pin per office.
//
// Progressive enhancement: the visible address list IS the marker source. Each
// office <li data-office-marker> carries data-lat / data-lng (geocoded server-
// side from its address) plus name/address for the popup, so with JS off the
// list still stands and with JS on the same data becomes the map. Adding an
// office in the CP adds a list item AND a pin — nothing to wire per location.

import L from "leaflet";
import "leaflet/dist/leaflet.css";

// On-brand pin (electric blue drop) as an inline SVG divIcon — no external image
// file to 404 and it inherits the crisp brand colour at any DPR.
const PIN = `<svg width="30" height="40" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M15 0C6.716 0 0 6.716 0 15c0 10.5 15 25 15 25s15-14.5 15-25C30 6.716 23.284 0 15 0Z" fill="#014df7"/><circle cx="15" cy="15" r="5.5" fill="#fff"/></svg>`;

const icon = L.divIcon({
  html: PIN,
  className: "offices-map__pin",
  iconSize: [30, 40],
  iconAnchor: [15, 40],
  popupAnchor: [0, -34],
});

function escapeHtml(str) {
  return String(str).replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]
  );
}

function popupHtml(el) {
  const name = el.dataset.name?.trim();
  const lines = [
    el.dataset.address,
    el.dataset.locality,
    el.dataset.country,
  ]
    .map((l) => (l || "").trim())
    .filter(Boolean);

  // The address is a click-out to the visitor's map app (same target as the list).
  const mapsUrl = el.dataset.mapsUrl?.trim();
  const linesHtml = lines.map((l) => `<span>${escapeHtml(l)}</span>`).join("");
  const address = mapsUrl
    ? `<a class="offices-map__popup-link" href="${escapeHtml(mapsUrl)}" target="_blank" rel="noopener">${linesHtml}</a>`
    : linesHtml;

  let html = '<div class="offices-map__popup">';
  if (name) html += `<strong>${escapeHtml(name)}</strong>`;
  if (lines.length) html += address;
  html += "</div>";
  return html;
}

function initOfficesMap() {
  const mapEl = document.querySelector("#officesMap");
  if (!mapEl || mapEl._leaflet_id) return;

  const markerEls = Array.from(
    document.querySelectorAll("[data-office-marker]")
  ).filter((el) => {
    const lat = parseFloat(el.dataset.lat);
    const lng = parseFloat(el.dataset.lng);
    return !Number.isNaN(lat) && !Number.isNaN(lng);
  });

  if (!markerEls.length) {
    mapEl.hidden = true; // nothing to plot (all geocodes failed) — leave the list
    return;
  }

  const map = L.map(mapEl, {
    scrollWheelZoom: false,
    // Zoom control bottom-right so an upward popup never overlaps it (markers near
    // the top-left open their bubble over a top-left control).
    zoomControl: false,
    // Attribution strip hidden per client request. Esri/OSM terms ask for credit,
    // so it is acknowledged here in source instead of on the map.
    attributionControl: false,
    dragging: !L.Browser.mobile,
    tap: false,
  });

  // Esri "Light Gray Canvas" — a free, no-API-key light basemap (minimal grey
  // land, muted borders) that suits the brand. Base + a separate reference layer
  // for place labels. Esri tiles use {z}/{y}/{x} order.
  L.control.zoom({ position: "bottomright" }).addTo(map);

  L.tileLayer(
    "https://services.arcgisonline.com/arcgis/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
    { maxZoom: 16 }
  ).addTo(map);
  L.tileLayer(
    "https://services.arcgisonline.com/arcgis/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}",
    { maxZoom: 16, pane: "overlayPane" }
  ).addTo(map);

  const group = L.featureGroup();
  markerEls.forEach((el) => {
    const lat = parseFloat(el.dataset.lat);
    const lng = parseFloat(el.dataset.lng);
    L.marker([lat, lng], { icon })
      .bindPopup(popupHtml(el), { closeButton: false, offset: [0, -4] })
      .addTo(group);
  });
  group.addTo(map);

  if (markerEls.length === 1) {
    map.setView(group.getBounds().getCenter(), 13);
  } else {
    map.fitBounds(group.getBounds(), { padding: [48, 48], maxZoom: 13 });
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initOfficesMap);
} else {
  initOfficesMap();
}
